/**
 * Varsity letters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const varsityLettersPack: ToilePack = {
    slug: 'varsity-letters',
    category: 'illustration',
    name: 'Varsity letters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'patch', label: 'Letter patch', subject: 'a single varsity letter "R" as a chenille patch, a thick block capital with angled serifs in two outline layers'},
        {key: 'team', label: 'Team name', subject: 'the word "TIGERS" in arched varsity block letters with a double outline and a small tiger paw beneath'},
        {key: 'number', label: 'Jersey number', subject: 'the jersey number "23" in tall varsity block numerals with a contrasting outline and the word "VARSITY" small above it'},
    ],
    styles: [
        {key: 'chenille', label: 'Chenille', style: 'chenille patch embroidery, raised felt-textured block letters with a stitched edge', palette: 'Maroon and gold on a plain light gray background.', framing: 'The patch is centered with a wide margin all round.'},
        {key: 'vector', label: 'Flat vector', style: 'clean flat vector lettering with a bold double outline, hard edges and no gradients', palette: 'Royal blue and white on a solid navy background.', framing: 'The design is centered with an even margin.'},
        {key: 'sticker', label: 'Die-cut sticker', style: 'a die-cut sticker with a thick white border around the shape and flat glossy fills', palette: 'Green and gold on a plain white background.', framing: 'The sticker is centered, well inside the frame.'},
    ],
    directions: ['green and gold', 'red and black', 'your initial'],
    directionHint: 'try: green and gold',
    seo: {
        title: 'Varsity Letters: Letter Patch, Team Name, Jersey Number',
        metaDescription: 'Nine varsity lettering designs: a chenille letter patch, a team name and a jersey number in two school colors.',
        h1: 'Varsity letters: a single letter patch, a team name, a jersey number',
        tagline: 'Collegiate block letters for jackets and jerseys, in three treatments.',
        intro: 'Varsity letters are the collegiate block letters of team jackets, jerseys and school spirit shirts. The nine designs show a single chenille letter R patch, the team word TIGERS in arched block letters with a double outline, and the jersey number 23 in tall block numerals. Each comes as chenille embroidery, flat vector, or a die-cut sticker. Ask for green and gold, red and black or your initial, and the block letters are set in those colors.',
        useCases: [
            'a team jacket patch design',
            'a school spirit shirt',
            'a jersey number for a sports team',
            'a sticker for a locker',
        ],
        faq: [
            {q: 'What is a chenille patch?', a: 'A raised, felt-textured letter with a stitched edge, the kind sewn onto a jacket.'},
            {q: 'Can I get a different team name?', a: 'Yes. Ask for another team word or an initial, and the block letters keep the same shape.'},
            {q: 'Are the colors fixed?', a: 'No. The samples use two school colors, and you can ask for a pair such as green and gold.'},
            {q: 'What does the jersey number look like?', a: 'A tall block numeral with a contrasting outline. The sample is 23.'},
        ],
    },
}
